import { useState } from "react";

function InstitutionalSections({ config, onShowPrivacy }) {
  const [activeProgram, setActiveProgram] = useState(null);

  const programs = [
    {
      id: "tribal-history",
      title: "Tribal History & Heritage",
      icon: "📜",
      description: "Explore the rich history of the Twelve Tribes of Yisrael from ancient times to the present day.",
      courses: ["Ancient Yisrael History", "Diaspora Studies", "Tribal Genealogy", "Historical Documentation"]
    },
    {
      id: "scriptural-studies",
      title: "Scriptural Studies",
      icon: "📖",
      description: "In-depth study of the Torah, Prophets, and Writings with emphasis on original Hebrew context.",
      courses: ["Torah Foundation", "Prophetic Literature", "Wisdom Books", "Hebrew Language Basics"]
    },
    {
      id: "cultural-identity",
      title: "Cultural Identity Education",
      icon: "🏛️",
      description: "Understanding and preserving the cultural practices, traditions, and identity of the Children of Yisrael.",
      courses: ["Cultural Preservation", "Traditional Practices", "Identity Studies", "Community Heritage"]
    },
    {
      id: "leadership",
      title: "Leadership Development",
      icon: "👑",
      description: "Preparing future leaders, ministers, and educators for community service and spiritual guidance.",
      courses: ["Ministry Training", "Community Leadership", "Teaching Methods", "Ordination Preparation"]
    },
    {
      id: "youth-learning",
      title: "Youth & Family Education",
      icon: "👨‍👩‍👧‍👦",
      description: "K-12 curriculum designed to educate young minds in heritage, faith, and academic excellence.",
      courses: ["K-5 Foundation", "Middle School Studies", "High School Advanced", "Family Devotionals"]
    }
  ];

  const impactStats = [
    { number: "2012", label: "Year Established", icon: "🏛️" },
    { number: "1000+", label: "Students Worldwide", icon: "🎓" },
    { number: "50+", label: "Courses Available", icon: "📚" },
    { number: "Global", label: "All Nations Welcome", icon: "🌍" }
  ];

  const communityInitiatives = [
    {
      title: "Youth Education Outreach",
      description: "Providing accessible education to young people seeking to understand their heritage and identity.",
      icon: "🎯"
    },
    {
      title: "Community Development",
      description: "Supporting local communities through educational resources and spiritual guidance programs.",
      icon: "🤝"
    },
    {
      title: "Digital Learning Access",
      description: "Making quality education available worldwide through our online learning platform.",
      icon: "🌍"
    },
    {
      title: "Ministry Training",
      description: "Preparing dedicated individuals for ordained ministry and community leadership roles.",
      icon: "📿"
    }
  ];

  return (
    <div className="institutional-sections">
      {/* Mission & Vision Section */}
      <section className="section mission-section" data-testid="mission-section">
        <div className="section-container">
          <div className="section-badge">Our Purpose</div>
          <h2 className="section-title">Mission & Vision</h2>
          
          <div className="mission-grid">
            <div className="mission-card">
              <div className="mission-icon">🎯</div>
              <h3>Our Mission</h3>
              <p>
                To provide comprehensive, faith-based education that teaches the heritage, 
                history, and scriptures of the Nation of Yisrael. We welcome all people of all 
                nations who seek Biblical truth and spiritual growth. We are committed to academic 
                excellence rooted in scriptural truth and historical accuracy.
              </p>
            </div>
            
            <div className="mission-card">
              <div className="mission-icon">🔭</div>
              <h3>Our Vision</h3>
              <p>
                To be a premier global educational institution open to all seekers of truth, 
                empowering students worldwide with knowledge of Biblical heritage, scripture, 
                and purpose. We envision a diverse community of educated, spiritually grounded 
                individuals united in the pursuit of wisdom.
              </p>
            </div>
            
            <div className="mission-card">
              <div className="mission-icon">💎</div>
              <h3>Our Values</h3>
              <p>
                Truth in education, respect for heritage, excellence in scholarship, community 
                unity, and spiritual integrity guide everything we do. We welcome all who seek 
                knowledge and believe in education that transforms minds and enriches lives.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Programs Section */}
      <section className="section programs-section" data-testid="programs-section">
        <div className="section-container">
          <div className="section-badge">Academic Programs</div>
          <h2 className="section-title">Learning Paths</h2>
          <p className="section-subtitle">
            Structured educational programs designed to provide comprehensive understanding 
            of Biblical heritage, scripture, and the teachings of Yisrael. Open to all nations.
          </p>
          
          <div className="programs-grid">
            {programs.map((program) => (
              <div 
                key={program.id}
                className={`program-card ${activeProgram === program.id ? 'active' : ''}`}
                onClick={() => setActiveProgram(activeProgram === program.id ? null : program.id)}
                data-testid={`program-${program.id}`}
              >
                <div className="program-icon">{program.icon}</div>
                <h3>{program.title}</h3>
                <p>{program.description}</p>
                
                {activeProgram === program.id && (
                  <div className="program-courses">
                    <h4>Featured Courses:</h4>
                    <ul>
                      {program.courses.map((course, idx) => (
                        <li key={idx}>{course}</li>
                      ))}
                    </ul>
                  </div>
                )}
                
                <span className="program-expand">
                  {activeProgram === program.id ? '− Less' : '+ Learn More'}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Impact Stats */}
      <section className="section stats-section" data-testid="stats-section">
        <div className="section-container">
          <div className="stats-grid">
            {impactStats.map((stat, idx) => (
              <div key={idx} className="stat-card">
                <div className="stat-icon">{stat.icon}</div>
                <div className="stat-number">{stat.number}</div>
                <div className="stat-label">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Community Impact Section */}
      <section className="section community-section" data-testid="community-section">
        <div className="section-container">
          <div className="section-badge">Making a Difference</div>
          <h2 className="section-title">Community Impact</h2>
          <p className="section-subtitle">
            Our commitment extends beyond the classroom to serve and uplift communities worldwide.
          </p>
          
          <div className="community-grid">
            {communityInitiatives.map((initiative, idx) => (
              <div key={idx} className="community-card">
                <div className="community-icon">{initiative.icon}</div>
                <h3>{initiative.title}</h3>
                <p>{initiative.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Institutional Credibility Section */}
      <section className="section credibility-section" data-testid="credibility-section">
        <div className="section-container">
          <div className="section-badge">Our Foundation</div>
          <h2 className="section-title">Institutional Credibility</h2>
          
          <div className="credibility-content">
            <div className="credibility-seal">
              <img 
                src="/assets/twelve_tribes_of_yisrael_official_seal.png" 
                alt="Twelve Tribes of Yisrael Official Seal"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
              <div className="seal-fallback">⭐</div>
            </div>
            
            <div className="credibility-info">
              <div className="credibility-item">
                <span className="credibility-label">Organization</span>
                <span className="credibility-value">{config?.CHURCH_NAME || "Twelve Tribes of Yisrael"}</span>
              </div>
              <div className="credibility-item">
                <span className="credibility-label">Established</span>
                <span className="credibility-value">{config?.ESTABLISHED || 2012}</span>
              </div>
              <div className="credibility-item">
                <span className="credibility-label">Leadership</span>
                <span className="credibility-value">Chief/Priest {config?.CHIEF_PRIEST || "Ykbn Yoonw"}</span>
              </div>
              <div className="credibility-item">
                <span className="credibility-label">Educational Focus</span>
                <span className="credibility-value">Heritage, Scripture, Identity, Ministry</span>
              </div>
              <div className="credibility-item">
                <span className="credibility-label">Accreditation</span>
                <span className="credibility-value">Private Religious Educational Institution</span>
              </div>
            </div>
            
            <div className="credibility-statement">
              <p>
                Yisrael Learning Center operates as a private religious educational institution 
                under the auspices of the Twelve Tribes of Yisrael organization. Our curriculum 
                is designed to provide faith-based education that honors our ancestral heritage 
                while meeting modern educational standards.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Professional Footer */}
      <footer className="institutional-footer" data-testid="institutional-footer">
        <div className="footer-container">
          <div className="footer-main">
            <div className="footer-brand">
              <h3>Yisrael Learning Center</h3>
              <p className="footer-tagline">Preserving Heritage • Educating Generations • Building Community</p>
              <p className="footer-org">{config?.CHURCH_NAME || "Twelve Tribes of Yisrael"}</p>
              <p className="footer-established">Established {config?.ESTABLISHED || 2012}</p>
            </div>
            
            <div className="footer-links">
              <div className="footer-column">
                <h4>Education</h4>
                <ul>
                  <li>K-12 Programs</li>
                  <li>Advanced Studies</li>
                  <li>Ministry Training</li>
                  <li>Ordination Program</li>
                </ul>
              </div>
              
              <div className="footer-column">
                <h4>Resources</h4>
                <ul>
                  <li>Resource Library</li>
                  <li>Curriculum Guide</li>
                  <li>Student Portal</li>
                  <li>Parent Portal</li>
                </ul>
              </div>
              
              <div className="footer-column">
                <h4>Contact</h4>
                <ul>
                  <li>
                    <a href="mailto:orgof12tribe@gmail.com" style={{ color: '#87CEEB' }}>
                      orgof12tribe@gmail.com
                    </a>
                  </li>
                  <li>
                    <a href="https://www.ylcenter.com" style={{ color: '#87CEEB' }}>
                      www.ylcenter.com
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          </div>
          
          <div className="footer-bottom">
            <div className="footer-legal">
              <button 
                onClick={onShowPrivacy}
                className="footer-link-btn"
              >
                Privacy Policy
              </button>
              <span className="footer-divider">|</span>
              <a 
                href="/verify-minister"
                className="footer-link-btn"
                style={{ background: "none", border: "none", color: "#87CEEB", cursor: "pointer" }}
              >
                Verify Minister
              </a>
              <span className="footer-divider">|</span>
              <button 
                onClick={onShowPrivacy}
                className="footer-link-btn"
              >
                Terms of Use
              </button>
              <span className="footer-divider">|</span>
              <span>Student Rights</span>
            </div>
            <div className="footer-copyright">
              © {new Date().getFullYear()} Yisrael Learning Center. All rights reserved.
            </div>
          </div>
        </div>
      </footer>

      <style>{`
        .institutional-sections {
          width: 100%;
        }
        
        .section {
          padding: 60px 20px;
        }
        
        .section-container {
          max-width: 1200px;
          margin: 0 auto;
        }
        
        .section-badge {
          display: inline-block;
          background: linear-gradient(135deg, #FFD700 0%, #FFA500 100%);
          color: #000;
          padding: 6px 16px;
          border-radius: 20px;
          font-size: 0.8rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 1px;
          margin-bottom: 15px;
        }
        
        .section-title {
          font-family: 'Playfair Display', serif;
          font-size: 2.5rem;
          color: #FFD700;
          margin: 0 0 15px 0;
          text-align: center;
        }
        
        .section-subtitle {
          color: #aaa;
          font-size: 1.1rem;
          text-align: center;
          max-width: 700px;
          margin: 0 auto 40px auto;
          line-height: 1.6;
        }
        
        /* Mission Section */
        .mission-section {
          background: linear-gradient(180deg, #0a0a0a 0%, #1a1a2e 100%);
        }
        
        .mission-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 25px;
          margin-top: 40px;
        }
        
        .mission-card {
          background: rgba(255, 255, 255, 0.03);
          border: 1px solid rgba(255, 215, 0, 0.2);
          border-radius: 16px;
          padding: 30px;
          transition: all 0.3s ease;
        }
        
        .mission-card:hover {
          transform: translateY(-5px);
          border-color: rgba(255, 215, 0, 0.5);
          box-shadow: 0 10px 40px rgba(255, 215, 0, 0.1);
        }
        
        .mission-icon {
          font-size: 2.5rem;
          margin-bottom: 15px;
        }
        
        .mission-card h3 {
          color: #FFD700;
          font-size: 1.3rem;
          margin: 0 0 15px 0;
        }
        
        .mission-card p {
          color: #ccc;
          line-height: 1.7;
          margin: 0;
        }
        
        /* Programs Section */
        .programs-section {
          background: #0f0f0f;
        }
        
        .programs-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 20px;
        }
        
        .program-card {
          background: linear-gradient(145deg, #1a1a1a 0%, #0a0a0a 100%);
          border: 1px solid #333;
          border-radius: 12px;
          padding: 25px;
          cursor: pointer;
          transition: all 0.3s ease;
        }
        
        .program-card:hover, .program-card.active {
          border-color: #FFD700;
          box-shadow: 0 5px 30px rgba(255, 215, 0, 0.15);
        }
        
        .program-icon {
          font-size: 2rem;
          margin-bottom: 12px;
        }
        
        .program-card h3 {
          color: #FFD700;
          font-size: 1.1rem;
          margin: 0 0 10px 0;
        }
        
        .program-card p {
          color: #aaa;
          font-size: 0.9rem;
          line-height: 1.5;
          margin: 0;
        }
        
        .program-courses {
          margin-top: 15px;
          padding-top: 15px;
          border-top: 1px solid #333;
        }
        
        .program-courses h4 {
          color: #87CEEB;
          font-size: 0.85rem;
          margin: 0 0 10px 0;
        }
        
        .program-courses ul {
          list-style: none;
          padding: 0;
          margin: 0;
        }
        
        .program-courses li {
          color: #ccc;
          font-size: 0.85rem;
          padding: 5px 0;
          padding-left: 15px;
          position: relative;
          background: transparent;
          border: none;
        }
        
        .program-courses li::before {
          content: "→";
          position: absolute;
          left: 0;
          color: #FFD700;
        }
        
        .program-expand {
          display: block;
          margin-top: 15px;
          color: #FFD700;
          font-size: 0.85rem;
          font-weight: 500;
        }
        
        /* Stats Section */
        .stats-section {
          background: linear-gradient(135deg, #1a1a2e 0%, #0a0a0a 100%);
          padding: 50px 20px;
        }
        
        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 30px;
        }
        
        .stat-card {
          text-align: center;
          padding: 20px;
        }
        
        .stat-icon {
          font-size: 2rem;
          margin-bottom: 10px;
        }
        
        .stat-number {
          font-family: 'Playfair Display', serif;
          font-size: 2.5rem;
          color: #FFD700;
          font-weight: 700;
        }
        
        .stat-label {
          color: #aaa;
          font-size: 0.9rem;
          margin-top: 5px;
        }
        
        /* Community Section */
        .community-section {
          background: #0a0a0a;
        }
        
        .community-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 25px;
        }
        
        .community-card {
          background: rgba(135, 206, 235, 0.05);
          border: 1px solid rgba(135, 206, 235, 0.2);
          border-radius: 12px;
          padding: 25px;
          text-align: center;
          transition: all 0.3s ease;
        }
        
        .community-card:hover {
          transform: translateY(-3px);
          border-color: rgba(135, 206, 235, 0.5);
        }
        
        .community-icon {
          font-size: 2.5rem;
          margin-bottom: 15px;
        }
        
        .community-card h3 {
          color: #87CEEB;
          font-size: 1.1rem;
          margin: 0 0 10px 0;
        }
        
        .community-card p {
          color: #aaa;
          font-size: 0.9rem;
          line-height: 1.5;
          margin: 0;
        }
        
        /* Credibility Section */
        .credibility-section {
          background: linear-gradient(180deg, #0f0f1a 0%, #0a0a0a 100%);
        }
        
        .credibility-content {
          display: grid;
          grid-template-columns: auto 1fr;
          gap: 40px;
          align-items: start;
          margin-top: 40px;
        }
        
        @media (max-width: 768px) {
          .credibility-content {
            grid-template-columns: 1fr;
            text-align: center;
          }
        }
        
        .credibility-seal {
          width: 150px;
          height: 150px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .credibility-seal img {
          max-width: 100%;
          max-height: 100%;
          border-radius: 50%;
          border: 3px solid #FFD700;
        }
        
        .seal-fallback {
          font-size: 4rem;
          display: none;
        }
        
        .credibility-seal img[style*="display: none"] + .seal-fallback {
          display: block;
        }
        
        .credibility-info {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 20px;
        }
        
        .credibility-item {
          background: rgba(255, 215, 0, 0.05);
          border-left: 3px solid #FFD700;
          padding: 15px 20px;
        }
        
        .credibility-label {
          display: block;
          color: #888;
          font-size: 0.8rem;
          text-transform: uppercase;
          letter-spacing: 1px;
          margin-bottom: 5px;
        }
        
        .credibility-value {
          color: #fff;
          font-size: 1rem;
          font-weight: 500;
        }
        
        .credibility-statement {
          grid-column: 1 / -1;
          margin-top: 20px;
          padding: 20px;
          background: rgba(255, 255, 255, 0.02);
          border-radius: 8px;
          border: 1px solid #333;
        }
        
        .credibility-statement p {
          color: #aaa;
          line-height: 1.7;
          margin: 0;
          text-align: center;
        }
        
        /* Footer */
        .institutional-footer {
          background: #000;
          border-top: 1px solid #333;
          padding: 50px 20px 30px;
        }
        
        .footer-container {
          max-width: 1200px;
          margin: 0 auto;
        }
        
        .footer-main {
          display: grid;
          grid-template-columns: 1.5fr 2fr;
          gap: 50px;
          margin-bottom: 40px;
        }
        
        @media (max-width: 768px) {
          .footer-main {
            grid-template-columns: 1fr;
            gap: 30px;
          }
        }
        
        .footer-brand h3 {
          color: #FFD700;
          font-family: 'Playfair Display', serif;
          font-size: 1.5rem;
          margin: 0 0 10px 0;
        }
        
        .footer-tagline {
          color: #87CEEB;
          font-size: 0.9rem;
          margin: 0 0 15px 0;
        }
        
        .footer-org, .footer-established {
          color: #888;
          font-size: 0.85rem;
          margin: 5px 0;
        }
        
        .footer-links {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 30px;
        }
        
        @media (max-width: 600px) {
          .footer-links {
            grid-template-columns: 1fr;
          }
        }
        
        .footer-column h4 {
          color: #FFD700;
          font-size: 0.95rem;
          margin: 0 0 15px 0;
          text-transform: uppercase;
          letter-spacing: 1px;
        }
        
        .footer-column ul {
          list-style: none;
          padding: 0;
          margin: 0;
        }
        
        .footer-column li {
          color: #aaa;
          font-size: 0.85rem;
          padding: 6px 0;
          background: transparent;
          border: none;
        }
        
        .footer-bottom {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding-top: 30px;
          border-top: 1px solid #222;
          flex-wrap: wrap;
          gap: 15px;
        }
        
        .footer-legal {
          display: flex;
          align-items: center;
          gap: 10px;
          flex-wrap: wrap;
        }
        
        .footer-link-btn {
          background: transparent;
          border: none;
          color: #87CEEB;
          font-size: 0.85rem;
          cursor: pointer;
          padding: 0;
          margin: 0;
        }
        
        .footer-link-btn:hover {
          text-decoration: underline;
        }
        
        .footer-divider {
          color: #888;
        }
        
        .footer-legal span {
          color: #999;
          font-size: 0.85rem;
        }
        
        .footer-copyright {
          color: #999;
          font-size: 0.8rem;
        }
      `}</style>
    </div>
  );
}

export default InstitutionalSections;
